import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import { useSnackbar } from "src/components/snackbar";
import { propuestasApi } from "src/services/propuestasApi";
import { obtenerNombreDestinatario } from "src/utils/propuestasV2/destinatario";
import { formatValorItem } from "src/utils/propuestasV2/formatValorItem";
import { formatearMonedaCliente } from "src/utils/formatearMonedaCliente";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
}));

const MOTIVO_MAX_LENGTH = 2000;

// Total vigente por moneda, para dar contexto de qué se está por enviar. No
// depende de ningún cálculo del servidor: es una suma directa de lo que ya
// trae el detalle de la propuesta.
function totalPorMoneda(items) {
  const totales = new Map();
  for (const item of items) {
    const moneda = formatearMonedaCliente(item.tipoMoneda) || "—";
    const anterior = totales.get(moneda) ?? 0;
    totales.set(moneda, anterior + Number(item.valorConDescuento ?? 0));
  }
  return totales;
}

/**
 * Envío de la ronda siguiente de una renegociación: cierra la conversación
 * abierta con `editarItemsRenegociacionV2` y le manda al cliente la versión
 * ajustada. `resumenAjuste` no sale de ningún endpoint de previsualización
 * -- no existe uno -- sino de acumular en memoria, mientras la pantalla de
 * ajuste sigue montada, los conteos que ya devuelve cada guardado
 * (`cambiosRegistrados`/`cantidadRetirados`/`cantidadAgregados`). Por eso es
 * `null` si el vendedor llega acá sin haber guardado ningún ajuste en esta
 * visita (por ejemplo, después de recargar la página): en ese caso se avisa
 * en vez de mostrar un cero que no significa "no cambió nada".
 *
 * Los servicios retirados sí son exactos y sobreviven a un recargo: cada
 * retiro queda escrito en el servicio con la ronda en la que va a salir
 * (`retiradoEnRonda`), así que se puede leer directo del detalle sin
 * depender de lo que se acumuló en esta sesión del navegador.
 */
export default function ModalEnvioRondaV2({
  open,
  setOpen,
  propuesta,
  resumenAjuste,
  onSuccess,
  onEnviado,
  maxWidth = "sm",
  fullWidth = true,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [enviarRonda] = propuestasApi.useEnviarRondaRenegociacionV2Mutation();

  // Comparte la misma entrada de caché que HiloNegociacion -- no dispara una
  // consulta nueva si esa vista ya está montada en la página.
  const { data: hilo, isSuccess: hiloListo } =
    propuestasApi.useGetHiloNegociacionV2Query(
      { id: propuesta?.id },
      { skip: !open || !propuesta?.id },
    );

  const items = Array.isArray(propuesta?.items) ? propuesta.items : [];
  const itemsRetirados = Array.isArray(propuesta?.itemsRetirados)
    ? propuesta.itemsRetirados
    : [];

  const ultimoNumeroEnviado = (hilo?.rondas ?? []).reduce(
    (max, ronda) => Math.max(max, Number(ronda.numero) || 0),
    0,
  );
  // Sin el hilo cargado no se sabe cuál es la última ronda enviada, así que
  // tampoco cuál es la próxima: mostrar "1" como adelanto sería afirmar un
  // número que todavía no se conoce.
  const numeroRondaPendiente = hiloListo ? ultimoNumeroEnviado + 1 : null;
  const retiradosEnEstaRonda =
    numeroRondaPendiente == null
      ? []
      : itemsRetirados.filter(
          (item) => item.retiradoEnRonda === numeroRondaPendiente,
        );

  const totalesVigentes = totalPorMoneda(items);

  const [message, setMessage] = useState("");
  const [motivo, setMotivo] = useState("");
  const [procesando, setProcesando] = useState(false);

  useEffect(() => {
    if (!open) {
      setMessage("");
      setMotivo("");
      setProcesando(false);
    }
  }, [open]);

  const handleClose = () => {
    if (!procesando) setOpen(false);
  };

  const handleConfirmar = async () => {
    setProcesando(true);
    try {
      // `message` viaja siempre, aunque esté vacío: la plantilla del correo
      // la usa sin default propio, así que omitir la clave cuando el
      // vendedor no escribe nada revienta la plantilla después de que la
      // ronda ya se escribió y el estado ya cambió -- el peor momento
      // posible para fallar, porque ya no hay transacción que revertir.
      const body = { message: message.trim() };
      if (motivo.trim()) body.motivo = motivo.trim();

      const resultado = await enviarRonda({
        id: propuesta.id,
        body,
      }).unwrap();

      // El servidor lanza en vez de devolver si el correo no salió, así que
      // llegar hasta acá ya significa que se envió.
      enqueueSnackbar(
        `Ronda ${resultado.ronda} enviada a ${resultado.correo}.`,
        {
          variant: "success",
        },
      );
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ||
          error?.message ||
          "Ocurrió un error al enviar la ronda.",
        { variant: "error" },
      );
      // El correo corre fuera de la transacción y el envío es fail-closed: si
      // el documento no se pudo archivar, nada de esto ocurrió, pero si lo
      // que falló fue el correo, la ronda ya quedó escrita y el estado ya
      // cambió en el servidor. En los dos casos el diálogo deja de tener
      // sentido abierto -- a diferencia del envío inicial, acá no hay un modo
      // bloqueado que necesite seguir mostrando algo -- así que se cierra
      // igual que si hubiera salido bien, y el refetch deja a la pantalla
      // mostrando el estado real en vez de un botón de enviar que ya no
      // corresponde.
      setOpen(false);
      onSuccess?.();
    } finally {
      setProcesando(false);
      // El resumen acumulado describe ajustes que, se haya enviado la ronda o
      // no, ya no son "lo próximo a enviar": si la ronda se creó, quedaron
      // reflejados en ella; si no se creó, el vendedor va a volver a la
      // pantalla de ajuste a revisar qué falló, no a reintentar el envío con
      // el mismo conteo.
      onEnviado?.();
    }
  };

  const nombreDestinatario = obtenerNombreDestinatario(propuesta);

  return (
    <Dialog
      open={open}
      maxWidth={maxWidth}
      onClose={handleClose}
      fullWidth={fullWidth}
    >
      <DialogTitle className={classes.dialogTitle}>
        {numeroRondaPendiente == null
          ? `Enviar ronda siguiente — Propuesta #${propuesta?.id}`
          : `Enviar ronda ${numeroRondaPendiente} — Propuesta #${propuesta?.id}`}
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={procesando}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Se renueva el enlace de la propuesta y se le manda a{" "}
            <strong>{nombreDestinatario}</strong> el documento con los servicios
            vigentes. Las decisiones que el cliente ya dejó sobre servicios
            observados o rechazados se reabren para que vuelva a pronunciarse;
            lo que ya aceptó se conserva.
          </Typography>
        </Alert>

        <Stack spacing={0.75} sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            Se enviarán {items.length}{" "}
            {items.length === 1 ? "servicio vigente" : "servicios vigentes"}
            {totalesVigentes.size > 0 && (
              <>
                {" "}
                por{" "}
                {[...totalesVigentes.entries()]
                  .map(([moneda, total]) => formatValorItem(moneda, total))
                  .join(" + ")}
              </>
            )}
            .
          </Typography>
          {retiradosEnEstaRonda.length > 0 && (
            <Typography variant="body2" color="textSecondary">
              {retiradosEnEstaRonda.length}{" "}
              {retiradosEnEstaRonda.length === 1
                ? "servicio sale"
                : "servicios salen"}{" "}
              de la propuesta en esta ronda:{" "}
              {retiradosEnEstaRonda
                .map((item) => item.faseTitulo || item.nombreServicio)
                .join(", ")}
              .
            </Typography>
          )}
          {resumenAjuste ? (
            <Typography variant="body2" color="textSecondary">
              En esta sesión registraste {resumenAjuste.cambiosRegistrados}{" "}
              {resumenAjuste.cambiosRegistrados === 1
                ? "cambio de precio o descripción"
                : "cambios de precio o descripción"}
              {resumenAjuste.cantidadAgregados > 0 &&
                ` y agregaste ${resumenAjuste.cantidadAgregados} ${
                  resumenAjuste.cantidadAgregados === 1
                    ? "servicio nuevo"
                    : "servicios nuevos"
                }`}
              .
            </Typography>
          ) : (
            <Typography variant="caption" color="textSecondary">
              No se registraron ajustes de servicios en esta sesión del
              navegador. Revisa los servicios vigentes antes de confirmar.
            </Typography>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Mensaje para el cliente (opcional)"
          placeholder="Ej: Ajustamos la propuesta según lo conversado..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={procesando}
          variant="outlined"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Motivo interno del ajuste (opcional)"
          placeholder="Ej: El cliente pidió bajar el precio del servicio de contabilidad..."
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          disabled={procesando}
          variant="outlined"
          inputProps={{ maxLength: MOTIVO_MAX_LENGTH }}
          helperText={`${motivo.length}/${MOTIVO_MAX_LENGTH} caracteres`}
        />

        {items.length === 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">
              No quedan servicios vigentes. El servidor va a rechazar el envío:
              agrega o reactiva al menos uno antes de continuar.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={handleClose} disabled={procesando}>
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={procesando}
          disabled={procesando || items.length === 0}
          onClick={handleConfirmar}
        >
          Enviar ronda
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

ModalEnvioRondaV2.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  propuesta: PropTypes.object.isRequired,
  resumenAjuste: PropTypes.shape({
    cambiosRegistrados: PropTypes.number,
    cantidadRetirados: PropTypes.number,
    cantidadAgregados: PropTypes.number,
  }),
  onSuccess: PropTypes.func,
  onEnviado: PropTypes.func,
  maxWidth: PropTypes.string,
  fullWidth: PropTypes.bool,
};

ModalEnvioRondaV2.defaultProps = {
  resumenAjuste: null,
  onSuccess: undefined,
  onEnviado: undefined,
  maxWidth: "sm",
  fullWidth: true,
};
